import React, { useState, useMemo } from 'react';
import type { SimWorld } from '../../simulation/types/SimWorld';
import { isGroupModeEnabled } from '../../simulation/types/SimWorld';
import type { SimNPC } from '../../simulation/types/SimNPC';
import type { GroupId, GroupConfig } from '../../simulation/types/Group';
import {
  compareDeck,
  calculateGenerationStats,
  analyzeGenerationChanges,
  DeckComparisonResult,
  GenerationDeckStats,
  GenerationStatsOptions,
} from '../../simulation/utils/DeckAnalyzer';

interface DeckAnalysisPanelProps {
  world: SimWorld;
  onNPCClick?: (npc: SimNPC) => void;
  isMobile?: boolean;
}

type TabType = 'compare' | 'generation';

type AliveFilterType = 'all' | 'alive' | 'dead';

export const DeckAnalysisPanel: React.FC<DeckAnalysisPanelProps> = ({ world, onNPCClick, isMobile = false }) => {
  const [activeTab, setActiveTab] = useState<TabType>('compare');
  const [selectedNPC1, setSelectedNPC1] = useState<SimNPC | null>(null);
  const [selectedNPC2, setSelectedNPC2] = useState<SimNPC | null>(null);
  const [selectedGeneration, setSelectedGeneration] = useState<number | null>(null);

  // フィルター状態
  const [aliveFilter, setAliveFilter] = useState<AliveFilterType>('alive');
  const [includeDeceased, setIncludeDeceased] = useState<boolean>(true);
  const [excludeImmigrants, setExcludeImmigrants] = useState<boolean>(false);
  const [filterGroup, setFilterGroup] = useState<GroupId | 'all'>('all');

  // グループモードの状態
  const groupModeActive = isGroupModeEnabled(world);
  const groups: GroupConfig[] = world.groupModeConfig?.groups || [];

  // フィルター適用済みNPCリスト（レーティング順）
  const filteredNPCs = useMemo(() => {
    return [...world.npcs]
      .filter(n => {
        // グループフィルタ
        if (groupModeActive && filterGroup !== 'all' && n.group !== filterGroup) {
          return false;
        }
        // 生存状態フィルタ
        if (aliveFilter === 'alive') return n.isAlive;
        if (aliveFilter === 'dead') return !n.isAlive;
        return true; // 'all'
      })
      .sort((a, b) => b.rating - a.rating);
  }, [world.npcs, aliveFilter, groupModeActive, filterGroup]);

  // 世代リスト
  const generations = useMemo(() => {
    const gens = new Set<number>();
    world.npcs.forEach(n => gens.add(n.generation || 1));
    return Array.from(gens).sort((a, b) => a - b);
  }, [world.npcs]);

  // デッキ比較結果
  const comparisonResult = useMemo(() => {
    if (!selectedNPC1 || !selectedNPC2) return null;
    return compareDeck(selectedNPC1, selectedNPC2);
  }, [selectedNPC1, selectedNPC2]);

  // 世代別統計（フィルターオプション適用）
  const generationStatsOptions: GenerationStatsOptions = useMemo(() => ({
    includeDeceased,
    excludeImmigrants,
  }), [includeDeceased, excludeImmigrants]);

  // 世代統計用のNPCリスト（グループフィルタ適用）
  const npcsForGeneration = useMemo(() => {
    if (!groupModeActive || filterGroup === 'all') {
      return world.npcs;
    }
    return world.npcs.filter(n => n.group === filterGroup);
  }, [world.npcs, groupModeActive, filterGroup]);

  const generationStats = useMemo(() => {
    return calculateGenerationStats(npcsForGeneration, generationStatsOptions);
  }, [npcsForGeneration, generationStatsOptions]);

  // 世代間変化
  const generationChanges = useMemo(() => {
    return analyzeGenerationChanges(generationStats);
  }, [generationStats]);

  return (
    <div style={styles.container}>
      {/* タブ */}
      <div style={styles.tabBar}>
        <button
          style={{
            ...styles.tabButton,
            ...(activeTab === 'compare' ? styles.tabButtonActive : {}),
          }}
          onClick={() => setActiveTab('compare')}
        >
          🔀 デッキ比較
        </button>
        <button
          style={{
            ...styles.tabButton,
            ...(activeTab === 'generation' ? styles.tabButtonActive : {}),
          }}
          onClick={() => setActiveTab('generation')}
        >
          📊 世代別分析
        </button>
      </div>

      {/* デッキ比較タブ */}
      {activeTab === 'compare' && (
        <div style={styles.content}>
          {/* フィルターコントロール */}
          <div style={styles.filterRow}>
            <span style={styles.filterLabel}>表示:</span>
            <select
              value={aliveFilter}
              onChange={e => setAliveFilter(e.target.value as AliveFilterType)}
              style={styles.filterSelect}
            >
              <option value="alive">生存中のみ</option>
              <option value="dead">死亡のみ</option>
              <option value="all">全員</option>
            </select>

            {/* グループフィルタ（グループモード有効時のみ表示） */}
            {groupModeActive && (
              <>
                <span style={styles.filterLabel}>グループ:</span>
                <select
                  value={filterGroup}
                  onChange={e => setFilterGroup(e.target.value as GroupId | 'all')}
                  style={styles.filterSelect}
                >
                  <option value="all">全グループ</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>

          <div style={styles.selectorRow}>
            <NPCSelector
              label="NPC 1"
              npcs={filteredNPCs}
              allNPCs={world.npcs}
              selected={selectedNPC1}
              onSelect={setSelectedNPC1}
              generations={generations}
            />
            <span style={styles.vsLabel}>VS</span>
            <NPCSelector
              label="NPC 2"
              npcs={filteredNPCs}
              allNPCs={world.npcs}
              selected={selectedNPC2}
              onSelect={setSelectedNPC2}
              generations={generations}
            />
          </div>

          {comparisonResult && selectedNPC1 && selectedNPC2 && (
            <ComparisonResult
              result={comparisonResult}
              npc1={selectedNPC1}
              npc2={selectedNPC2}
            />
          )}
        </div>
      )}

      {/* 世代別分析タブ */}
      {activeTab === 'generation' && (
        <div style={styles.content}>
          {/* 世代別統計フィルター */}
          <div style={styles.filterRow}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={includeDeceased}
                onChange={e => setIncludeDeceased(e.target.checked)}
                style={styles.checkbox}
              />
              死亡NPCを含める
            </label>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={excludeImmigrants}
                onChange={e => setExcludeImmigrants(e.target.checked)}
                style={styles.checkbox}
              />
              移住者を除外
            </label>

            {/* グループフィルタ（グループモード有効時のみ表示） */}
            {groupModeActive && (
              <>
                <span style={styles.filterLabel}>グループ:</span>
                <select
                  value={filterGroup}
                  onChange={e => setFilterGroup(e.target.value as GroupId | 'all')}
                  style={styles.filterSelect}
                >
                  <option value="all">全グループ</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>

          <div style={styles.genSelector}>
            <span style={styles.genLabel}>世代を選択:</span>
            {generationStats.map(stat => (
              <button
                key={stat.generation}
                style={{
                  ...styles.genButton,
                  ...(selectedGeneration === stat.generation ? styles.genButtonActive : {}),
                }}
                onClick={() => setSelectedGeneration(
                  selectedGeneration === stat.generation ? null : stat.generation
                )}
              >
                第{stat.generation}世代 ({stat.npcCount}人)
              </button>
            ))}
          </div>

          {selectedGeneration && generationStats.find(s => s.generation === selectedGeneration) ? (
            <GenerationDetail
              stats={generationStats.find(s => s.generation === selectedGeneration)!}
              changes={generationChanges.find(c => c.toGen === selectedGeneration)}
            />
          ) : (
            <GenerationOverview stats={generationStats} changes={generationChanges} />
          )}
        </div>
      )}
    </div>
  );
};

// NPC選択コンポーネント
interface NPCSelectorProps {
  label: string;
  npcs: SimNPC[];
  allNPCs: SimNPC[];
  selected: SimNPC | null;
  onSelect: (npc: SimNPC | null) => void;
  generations: number[];
}

const NPCSelector: React.FC<NPCSelectorProps> = ({ label, npcs, allNPCs, selected, onSelect, generations }) => {
  const [filterGen, setFilterGen] = useState<number | 'all'>('all');

  const filteredNPCs = useMemo(() => {
    if (filterGen === 'all') return npcs;
    return npcs.filter(n => (n.generation || 1) === filterGen);
  }, [npcs, filterGen]);

  return (
    <div style={styles.selector}>
      <div style={styles.selectorHeader}>
        <span style={styles.selectorLabel}>{label}</span>
        <select
          value={filterGen}
          onChange={e => setFilterGen(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
          style={styles.genFilter}
        >
          <option value="all">全世代</option>
          {generations.map(g => (
            <option key={g} value={g}>第{g}世代</option>
          ))}
        </select>
      </div>
      <select
        value={selected?.id || ''}
        onChange={e => {
          // allNPCsから検索（フィルタで除外されたNPCも選択できるように）
          const npc = allNPCs.find(n => n.id === e.target.value);
          onSelect(npc || null);
        }}
        style={styles.npcSelect}
      >
        <option value="">-- 選択 --</option>
        {filteredNPCs.map(npc => (
          <option key={npc.id} value={npc.id}>
            {npc.name} (Rating: {npc.rating}, 第{npc.generation || 1}世代){!npc.isAlive ? ' [死亡]' : ''}
          </option>
        ))}
      </select>
      {selected && (
        <div style={styles.selectedInfo}>
          <div>Rating: {selected.rating} {!selected.isAlive && <span style={{ color: '#888' }}>[死亡]</span>}</div>
          <div>戦績: {selected.wins}勝{selected.losses}敗</div>
        </div>
      )}
    </div>
  );
};

// 比較結果表示
interface ComparisonResultProps {
  result: DeckComparisonResult;
  npc1: SimNPC;
  npc2: SimNPC;
}

const ComparisonResult: React.FC<ComparisonResultProps> = ({ result, npc1, npc2 }) => {
  const commonCardsList = Object.entries(result.commonCards)
    .sort((a, b) => b[1].count - a[1].count);
  const deck1OnlyList = Object.entries(result.deck1OnlyCards)
    .sort((a, b) => b[1].count - a[1].count);
  const deck2OnlyList = Object.entries(result.deck2OnlyCards)
    .sort((a, b) => b[1].count - a[1].count);

  return (
    <div style={styles.resultContainer}>
      {/* 類似度 */}
      <div style={styles.similarityBox}>
        <div style={styles.similarityLabel}>類似度</div>
        <div style={{
          ...styles.similarityValue,
          color: result.similarity >= 50 ? '#4ade80' : result.similarity >= 30 ? '#fbbf24' : '#ef4444',
        }}>
          {result.similarity}%
        </div>
      </div>

      {/* 属性比較 */}
      <div style={styles.section}>
        <h4 style={styles.sectionTitle}>属性構成</h4>
        <div style={styles.attrCompare}>
          <div style={styles.attrColumn}>
            <div style={styles.attrHeader}>{npc1.name}</div>
            {Object.entries(result.attributeComparison.deck1)
              .sort((a, b) => b[1] - a[1])
              .map(([attr, count]) => (
                <div key={attr} style={styles.attrRow}>
                  <span>{attr}</span>
                  <span>{count}枚</span>
                </div>
              ))}
          </div>
          <div style={styles.attrColumn}>
            <div style={styles.attrHeader}>{npc2.name}</div>
            {Object.entries(result.attributeComparison.deck2)
              .sort((a, b) => b[1] - a[1])
              .map(([attr, count]) => (
                <div key={attr} style={styles.attrRow}>
                  <span>{attr}</span>
                  <span>{count}枚</span>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* 共通カード */}
      <div style={styles.section}>
        <h4 style={styles.sectionTitle}>共通カード ({commonCardsList.length}種)</h4>
        <div style={styles.cardList}>
          {commonCardsList.slice(0, 10).map(([id, card]) => (
            <div key={id} style={styles.cardItem}>
              <span style={styles.cardName}>{card.name}</span>
              <span style={styles.cardCount}>x{card.count}</span>
            </div>
          ))}
          {commonCardsList.length > 10 && (
            <div style={styles.moreText}>...他{commonCardsList.length - 10}種</div>
          )}
        </div>
      </div>

      {/* 専用カード */}
      <div style={styles.exclusiveSection}>
        <div style={styles.exclusiveColumn}>
          <h4 style={styles.sectionTitle}>{npc1.name}専用 ({deck1OnlyList.length}種)</h4>
          <div style={styles.cardList}>
            {deck1OnlyList.slice(0, 8).map(([id, card]) => (
              <div key={id} style={styles.cardItem}>
                <span style={styles.cardName}>{card.name}</span>
                <span style={styles.cardCount}>x{card.count}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={styles.exclusiveColumn}>
          <h4 style={styles.sectionTitle}>{npc2.name}専用 ({deck2OnlyList.length}種)</h4>
          <div style={styles.cardList}>
            {deck2OnlyList.slice(0, 8).map(([id, card]) => (
              <div key={id} style={styles.cardItem}>
                <span style={styles.cardName}>{card.name}</span>
                <span style={styles.cardCount}>x{card.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// 世代詳細表示
interface GenerationDetailProps {
  stats: GenerationDeckStats;
  changes?: {
    fromGen: number;
    toGen: number;
    increased: Array<{ name: string; change: number }>;
    decreased: Array<{ name: string; change: number }>;
  };
}

const GenerationDetail: React.FC<GenerationDetailProps> = ({ stats, changes }) => {
  return (
    <div style={styles.genDetail}>
      <div style={styles.genHeader}>
        <h3 style={styles.genTitle}>第{stats.generation}世代</h3>
        <div style={styles.genStats}>
          <span>人数: {stats.npcCount}人</span>
          <span>平均Rating: {stats.avgRating}</span>
          <span>平均勝率: {stats.avgWinRate}%</span>
        </div>
      </div>

      {/* 前世代からの変化 */}
      {changes && (
        <div style={styles.changesSection}>
          <h4 style={styles.sectionTitle}>第{changes.fromGen}世代からの変化</h4>
          <div style={styles.changesGrid}>
            <div style={styles.changesColumn}>
              <div style={styles.changesLabel}>📈 採用率UP</div>
              {changes.increased.length > 0 ? (
                changes.increased.map(c => (
                  <div key={c.name} style={styles.changeItem}>
                    <span>{c.name}</span>
                    <span style={{ color: '#4ade80' }}>+{c.change}%</span>
                  </div>
                ))
              ) : (
                <div style={styles.noChange}>大きな変化なし</div>
              )}
            </div>
            <div style={styles.changesColumn}>
              <div style={styles.changesLabel}>📉 採用率DOWN</div>
              {changes.decreased.length > 0 ? (
                changes.decreased.map(c => (
                  <div key={c.name} style={styles.changeItem}>
                    <span>{c.name}</span>
                    <span style={{ color: '#ef4444' }}>{c.change}%</span>
                  </div>
                ))
              ) : (
                <div style={styles.noChange}>大きな変化なし</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* トップカード */}
      <div style={styles.section}>
        <h4 style={styles.sectionTitle}>採用率トップ20</h4>
        <div style={styles.topCardsList}>
          {stats.topCards.map((card, idx) => (
            <div key={card.cardId} style={styles.topCardItem}>
              <span style={styles.topCardRank}>#{idx + 1}</span>
              <span style={styles.topCardName}>{card.name}</span>
              <span style={styles.topCardRate}>{card.usageRate.toFixed(0)}%</span>
              <span style={styles.topCardAvg}>平均{card.avgCount.toFixed(1)}枚</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// 世代全体オーバービュー
interface GenerationOverviewProps {
  stats: GenerationDeckStats[];
  changes: Array<{
    fromGen: number;
    toGen: number;
    increased: Array<{ name: string; change: number }>;
    decreased: Array<{ name: string; change: number }>;
  }>;
}

const GenerationOverview: React.FC<GenerationOverviewProps> = ({ stats, changes }) => {
  return (
    <div style={styles.overviewContainer}>
      <h3 style={styles.overviewTitle}>世代別サマリー</h3>
      <div style={styles.overviewTable}>
        <div style={styles.overviewHeader}>
          <span style={styles.overviewCol}>世代</span>
          <span style={styles.overviewCol}>人数</span>
          <span style={styles.overviewCol}>平均Rating</span>
          <span style={styles.overviewCol}>平均勝率</span>
          <span style={styles.overviewColWide}>トップ3カード</span>
        </div>
        {stats.map(stat => (
          <div key={stat.generation} style={styles.overviewRow}>
            <span style={styles.overviewCol}>第{stat.generation}世代</span>
            <span style={styles.overviewCol}>{stat.npcCount}人</span>
            <span style={styles.overviewCol}>{stat.avgRating}</span>
            <span style={styles.overviewCol}>{stat.avgWinRate}%</span>
            <span style={styles.overviewColWide}>
              {stat.topCards.slice(0, 3).map(c => c.name).join(', ')}
            </span>
          </div>
        ))}
      </div>

      {/* 世代間変化サマリー */}
      <h3 style={{ ...styles.overviewTitle, marginTop: '24px' }}>世代間の変化</h3>
      {changes.map(change => (
        <div key={`${change.fromGen}-${change.toGen}`} style={styles.changeSummary}>
          <div style={styles.changeHeader}>
            第{change.fromGen}世代 → 第{change.toGen}世代
          </div>
          <div style={styles.changeContent}>
            {change.increased.length > 0 && (
              <span style={{ color: '#4ade80' }}>
                📈 {change.increased.map(c => c.name).join(', ')}
              </span>
            )}
            {change.decreased.length > 0 && (
              <span style={{ color: '#ef4444', marginLeft: '12px' }}>
                📉 {change.decreased.map(c => c.name).join(', ')}
              </span>
            )}
            {change.increased.length === 0 && change.decreased.length === 0 && (
              <span style={{ color: '#666' }}>大きな変化なし</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  tabBar: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
  },
  tabButton: {
    padding: '8px 16px',
    background: '#2a2a3e',
    border: '1px solid #444',
    borderRadius: '6px',
    color: '#aaa',
    cursor: 'pointer',
    fontSize: '14px',
  },
  tabButtonActive: {
    background: '#3a3a5e',
    color: '#fff',
    borderColor: '#667eea',
  },
  content: {
    flex: 1,
    overflow: 'auto',
  },
  selectorRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '20px',
  },
  selector: {
    flex: 1,
    background: '#16162a',
    padding: '12px',
    borderRadius: '8px',
  },
  selectorHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  selectorLabel: {
    fontWeight: 'bold',
    color: '#ddd',
  },
  genFilter: {
    padding: '4px 8px',
    background: '#2a2a3e',
    border: '1px solid #444',
    borderRadius: '4px',
    color: '#ddd',
    fontSize: '12px',
  },
  npcSelect: {
    width: '100%',
    padding: '8px',
    background: '#2a2a3e',
    border: '1px solid #444',
    borderRadius: '4px',
    color: '#ddd',
    fontSize: '13px',
  },
  selectedInfo: {
    marginTop: '8px',
    fontSize: '12px',
    color: '#888',
  },
  vsLabel: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#667eea',
    alignSelf: 'center',
    marginTop: '30px',
  },
  resultContainer: {
    background: '#16162a',
    padding: '16px',
    borderRadius: '8px',
  },
  similarityBox: {
    textAlign: 'center',
    marginBottom: '20px',
    padding: '16px',
    background: '#1a1a2e',
    borderRadius: '8px',
  },
  similarityLabel: {
    fontSize: '14px',
    color: '#888',
    marginBottom: '4px',
  },
  similarityValue: {
    fontSize: '48px',
    fontWeight: 'bold',
  },
  section: {
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '14px',
    color: '#aaa',
    marginBottom: '8px',
    borderBottom: '1px solid #333',
    paddingBottom: '4px',
  },
  attrCompare: {
    display: 'flex',
    gap: '20px',
  },
  attrColumn: {
    flex: 1,
  },
  attrHeader: {
    fontWeight: 'bold',
    color: '#ddd',
    marginBottom: '8px',
  },
  attrRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px',
    color: '#aaa',
    padding: '2px 0',
  },
  cardList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  cardItem: {
    background: '#2a2a3e',
    padding: '4px 10px',
    borderRadius: '4px',
    fontSize: '12px',
    color: '#ddd',
  },
  cardName: {
    marginRight: '6px',
  },
  cardCount: {
    color: '#888',
  },
  moreText: {
    fontSize: '12px',
    color: '#666',
    alignSelf: 'center',
  },
  exclusiveSection: {
    display: 'flex',
    gap: '20px',
  },
  exclusiveColumn: {
    flex: 1,
  },
  genSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  genLabel: {
    fontSize: '14px',
    color: '#888',
  },
  genButton: {
    padding: '6px 12px',
    background: '#2a2a3e',
    border: '1px solid #444',
    borderRadius: '4px',
    color: '#aaa',
    cursor: 'pointer',
    fontSize: '12px',
  },
  genButtonActive: {
    background: '#3a3a5e',
    color: '#fff',
    borderColor: '#667eea',
  },
  genDetail: {
    background: '#16162a',
    padding: '16px',
    borderRadius: '8px',
  },
  genHeader: {
    marginBottom: '16px',
  },
  genTitle: {
    fontSize: '18px',
    color: '#fff',
    marginBottom: '8px',
  },
  genStats: {
    display: 'flex',
    gap: '20px',
    fontSize: '14px',
    color: '#aaa',
  },
  changesSection: {
    marginBottom: '20px',
    padding: '12px',
    background: '#1a1a2e',
    borderRadius: '6px',
  },
  changesGrid: {
    display: 'flex',
    gap: '20px',
  },
  changesColumn: {
    flex: 1,
  },
  changesLabel: {
    fontSize: '13px',
    color: '#888',
    marginBottom: '8px',
  },
  changeItem: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px',
    color: '#ddd',
    padding: '4px 0',
  },
  noChange: {
    fontSize: '12px',
    color: '#666',
  },
  topCardsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  topCardItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '6px 10px',
    background: '#1a1a2e',
    borderRadius: '4px',
    fontSize: '13px',
  },
  topCardRank: {
    width: '30px',
    color: '#667eea',
    fontWeight: 'bold',
  },
  topCardName: {
    flex: 1,
    color: '#ddd',
  },
  topCardRate: {
    width: '50px',
    textAlign: 'right',
    color: '#4ade80',
    fontWeight: 'bold',
  },
  topCardAvg: {
    width: '80px',
    textAlign: 'right',
    color: '#888',
    fontSize: '12px',
  },
  overviewContainer: {
    background: '#16162a',
    padding: '16px',
    borderRadius: '8px',
  },
  overviewTitle: {
    fontSize: '16px',
    color: '#fff',
    marginBottom: '12px',
  },
  overviewTable: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  overviewHeader: {
    display: 'flex',
    padding: '8px 12px',
    background: '#1a1a2e',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#888',
  },
  overviewRow: {
    display: 'flex',
    padding: '8px 12px',
    background: '#2a2a3e',
    borderRadius: '4px',
    fontSize: '13px',
    color: '#ddd',
  },
  overviewCol: {
    width: '100px',
    flexShrink: 0,
  },
  overviewColWide: {
    flex: 1,
    fontSize: '12px',
    color: '#aaa',
  },
  changeSummary: {
    padding: '12px',
    background: '#1a1a2e',
    borderRadius: '6px',
    marginBottom: '8px',
  },
  changeHeader: {
    fontSize: '13px',
    fontWeight: 'bold',
    color: '#ddd',
    marginBottom: '6px',
  },
  changeContent: {
    fontSize: '12px',
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '12px',
    padding: '8px 12px',
    background: '#1a1a2e',
    borderRadius: '6px',
  },
  filterLabel: {
    fontSize: '13px',
    color: '#888',
  },
  filterSelect: {
    padding: '4px 8px',
    background: '#2a2a3e',
    border: '1px solid #444',
    borderRadius: '4px',
    color: '#ddd',
    fontSize: '13px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#aaa',
    cursor: 'pointer',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
  },
};
